import { Search, X } from 'lucide-react';
import { useCallback, useRef, type Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { IconButton } from '@/components/ui/icon-button';
import { TextField } from '@/components/ui/text-field';

/**
 * THE BOX THAT NARROWS A LIST.
 *
 * ── WHY IT IS A COMPONENT AND NOT A RECIPE ────────────────────────────────────────────────────
 * `TextField` has carried `leadingIcon` and `trailing` for a while, so every search box in the
 * app COULD have looked the same. Four were written and four came out different: the problems
 * panel has a glass and a clear button, the media library a glass and no way to empty it, the
 * dashboard a hand-rolled `<input>` with its own height, radius and background, and the reaction
 * structures palette a bare field with nothing at all. Reported as *"it's visually very different
 * from the others"*. Slots are not a shared control — they are four chances to assemble one
 * differently, which is the fork this codebase keeps relearning.
 *
 * ── CLEARING PUTS THE CARET BACK ──────────────────────────────────────────────────────────────
 * The clear button is INSIDE the field and only exists while there is something to clear, so
 * pressing it removes the element that is focused. Focus then falls to `<body>` and a keyboard
 * user loses their place in the panel entirely — one Tab from the top of the document, not one
 * from the search box. So the field is refocused after it empties. That is the behaviour worth
 * sharing, and the reason the clear button cannot just be a prop every caller passes.
 *
 * ── NO PLACEHOLDER BY DEFAULT ─────────────────────────────────────────────────────────────────
 * The standing rule is that inputs spawn empty: ghost text reads as content. The glass already
 * says what the box is for, and the accessible name says it to a reader. `placeholder` is here
 * only for the two boxes that were given one deliberately (the media library's "Search your
 * images" names a SCOPE the glass cannot), and there is no default.
 */
export function SearchField({
  value, onChange, ariaLabel, clearLabel, placeholder, disabled, className = '', inputRef,
}: {
  value: string;
  onChange: (value: string) => void;
  /** Names the box itself: "Search structures", not "Search" — see `docs/accessible-names.md`. */
  ariaLabel: string;
  /** Overrides the clear button's name where the screen calls this a filter rather than a search. */
  clearLabel?: string;
  /** Only where the box needs to say something the glass cannot, such as which library it searches. */
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /**
   * For a caller that focuses the box itself — the media library focuses it when the grid opens.
   *
   * There is deliberately no `autoFocus`: a search box that takes the caret on mount steals it
   * from whatever opened the panel, and the one caller that wants focus here moves it itself,
   * after the thing it belongs to has rendered.
   */
  inputRef?: Ref<HTMLInputElement>;
}) {
  const { t } = useTranslation();
  const own = useRef<HTMLInputElement | null>(null);

  /* Both refs on one input. The caller's is honoured as given — it may be an object ref or a
     callback — while this component keeps its own handle for the refocus above. */
  const setRef = useCallback((node: HTMLInputElement | null) => {
    own.current = node;
    if (typeof inputRef === 'function') inputRef(node);
    else if (inputRef) (inputRef as { current: HTMLInputElement | null }).current = node;
  }, [inputRef]);

  const clear = clearLabel ?? t('common.clearSearch');

  return (
    <TextField
      variant='prose'
      value={value}
      onChange={onChange}
      ariaLabel={ariaLabel}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      inputRef={setRef}
      leadingIcon={<Search aria-hidden='true' className='h-4 w-4' />}
      /* Something is ALWAYS in the trailing slot, so the field reserves its right padding whether
         or not there is anything to clear. With the slot empty the padding drops from 36px to
         12px on the first keystroke, and a query long enough to fill the box slides sideways as
         it is typed. The spacer is inert and unspoken. */
      trailing={value ? (
        <IconButton
          size='xs'
          type='button'
          aria-label={clear}
          title={clear}
          /* `onMouseDown` is prevented so the press never takes focus off the input in the first
             place; the `focus()` below is for the keyboard path, where the button really is the
             focused element when it disappears. */
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { onChange(''); own.current?.focus(); }}
        >
          <X aria-hidden='true' className='h-3.5 w-3.5' />
        </IconButton>
      ) : <span aria-hidden='true' className='block h-6 w-6' />}
    />
  );
}
